import log from 'utils/log';

/**
 * Outgoing VOIP camera video is only ever shown as a small tile, so each video sender is capped to
 * fit a 320×240 box (either orientation), 15 fps and 300 kbps. With the cap,
 * `qualityLimitationReason: 'bandwidth'` on outbound video is expected and is not a fault.
 * WebKit ignores `maxFramerate` (~30 fps) but still holds the bitrate and resolution caps.
 */
export const MAX_LONG_SIDE = 320;
export const MAX_SHORT_SIDE = 240;
export const MAX_FRAMERATE = 15;
export const MAX_BITRATE = 300_000;

// Chromium rejects a setParameters that overlaps another on the same sender (InvalidStateError).
const queues = new WeakMap<RTCRtpSender, Promise<void>>();

/** Down-scale factor that fits the track into the box; 1 when its size is unknown. */
export function computeScaleDownBy({ width, height }: MediaTrackSettings): number {
  if (!width || !height) return 1;
  return Math.max(1, Math.max(width, height) / MAX_LONG_SIDE, Math.min(width, height) / MAX_SHORT_SIDE);
}

async function setLimits(sender: RTCRtpSender, track: MediaStreamTrack): Promise<void> {
  const params = sender.getParameters();
  const [encoding] = params.encodings ?? [];
  if (!encoding) {
    log.warn('[VOIP] Video sender has no encodings; send limits skipped');
    return;
  }
  encoding.maxBitrate = MAX_BITRATE;
  encoding.maxFramerate = MAX_FRAMERATE;
  encoding.scaleResolutionDownBy = computeScaleDownBy(track.getSettings?.() ?? {});
  await sender.setParameters(params);
}

/** Queue the send caps for a video track just placed on `sender`. Never throws. */
export function applyVideoSendLimits(sender: RTCRtpSender | undefined, track: MediaStreamTrack): void {
  if (track.kind !== 'video' || typeof sender?.getParameters !== 'function') return;
  const tail = (queues.get(sender) ?? Promise.resolve())
    .then(() => setLimits(sender, track))
    .catch((err: unknown) => log.warn('[VOIP] Could not apply video send limits', err));
  queues.set(sender, tail);
}
